import { useCallback, useMemo } from 'react'

import { useIntl } from 'react-intl'

import { getCategoriesFromSubcategories } from '@b-stock/fe-core/bstock-react'
import { ListingEnums } from '@b-stock/fe-core/locale'
import type { AvailableFiltersDto } from '@b-stock/order-process-api-client'

const SUPPORTED_FILTER_KEYS = [
  'attributesBrandName',
  'attributesModel',
  'categories',
  'itemCondition',
  'otherCategory',
]

type ManifestFilterItem = {
  // Raw facet values applied when this option is selected. The manifest API's
  // `categories` facet/filter operates on subcategory-level values, so a
  // top-level Category option must carry every subcategory value it covers.
  apiValues: string[]
  label: string
  value: string
}

type ManifestFilterConfig = {
  filterKey: string
  items: ManifestFilterItem[]
  title: string
}

type SubcategoryValue = Parameters<
  typeof getCategoriesFromSubcategories
>[0][number]

export const useManifestFilterConfigs = (
  availableFilters: AvailableFiltersDto[]
): ManifestFilterConfig[] => {
  const intl = useIntl()
  const formatCategory = ListingEnums.Category.useFormat()
  const formatItemCondition = ListingEnums.ItemCondition.useFormat()

  const formatTitle = useCallback(
    (key: string) =>
      intl.formatMessage({
        id: `Listing.manifestAgGrid.filterBar.title.${key}`,
        defaultMessage: key,
      }),
    [intl]
  )

  const formatValue = useCallback(
    (filterKey: string, value: string) => {
      switch (filterKey) {
        case 'itemCondition':
          return formatItemCondition(
            value as Parameters<typeof formatItemCondition>[0]
          )
        default:
          return value
      }
    },
    [formatItemCondition]
  )

  // The `categories` facet values are subcategories; the dropdown must offer
  // the listing's top-level categories, each expanding to the facet values it
  // covers. Unmapped values fall back to a standalone option so they remain
  // filterable.
  const buildCategoryItems = useCallback(
    (values: string[]): ManifestFilterItem[] => {
      const apiValuesByCategory = new Map<string, string[]>()
      values.forEach((value) => {
        const [category = value] = getCategoriesFromSubcategories([
          value as SubcategoryValue,
        ])
        apiValuesByCategory.set(category, [
          ...(apiValuesByCategory.get(category) ?? []),
          value,
        ])
      })
      return [...apiValuesByCategory.entries()]
        .map(([category, apiValues]) => ({
          apiValues,
          label: formatCategory(
            category as Parameters<typeof formatCategory>[0]
          ),
          value: category,
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
    },
    [formatCategory]
  )

  return useMemo(
    () =>
      availableFilters
        .filter(
          (f) => SUPPORTED_FILTER_KEYS.includes(f.key) && f.values.length > 0
        )
        .map((f) => ({
          filterKey: f.key,
          items:
            f.key === 'categories'
              ? buildCategoryItems(f.values)
              : f.values.map((v) => ({
                  apiValues: [v],
                  label: formatValue(f.key, v),
                  value: v,
                })),
          title: formatTitle(f.key),
        })),
    [availableFilters, buildCategoryItems, formatTitle, formatValue]
  )
}
